/**
 * entityResolutionReview.jsx — S2 (Oct 16, 2026) review pieces for the admin
 * Query Resolution tab:
 *   - why a search was flagged (reason chips) and the candidates it could have
 *     meant, each with a one-click "Use" that confirms it for everyone;
 *   - learned market aliases waiting for review (confirm / reject).
 * Presentational only: the tab owns fetching and passes callbacks.
 */
import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';

/** Statuses the Review Queue lists: S2 `pending`, legacy `auto`, and user reports. */
export const REVIEW_QUEUE_STATUSES = ['pending', 'auto', 'user_reported'];

const REASONS = {
  needs_review: { label: '0 results', color: '#ef4444' },
  low_confidence: { label: 'Low confidence', color: '#f59e0b' },
  ambiguous_player: { label: 'Several players match', color: '#f59e0b' },
  ambiguous_team: { label: 'Several teams match', color: '#f59e0b' },
  llm_guess: { label: 'AI guessed', color: '#a78bfa' },
  guard_rejected: { label: 'Saved answer rejected', color: '#38bdf8' },
  sport_mismatch: { label: 'Sport mismatch', color: '#f59e0b' },
  team_conflict: { label: 'Roster vs live game', color: '#f59e0b' },
};

export function flagReasonLabel(reason) {
  return REASONS[reason] || { label: reason ? String(reason).replace(/_/g, ' ') : 'Flagged', color: 'rgba(255,255,255,0.6)' };
}

/** Every reason a flag has collected (newer docs keep flag_reasons; older ones one flag_reason). */
export function flagReasonsOf(fq) {
  const list = Array.isArray(fq?.flag_reasons) && fq.flag_reasons.length ? fq.flag_reasons : [fq?.flag_reason];
  return [...new Set(list.filter(Boolean))];
}

const SOURCE_LABELS = {
  detection: 'picked', llm: 'AI answer', offered_to_llm: 'offered to AI', query_fast_paths: 'saved query',
  entity_resolutions: 'saved resolution', n2: 'team match', odds_cache: 'team match', props_index: 'roster', players_cache: 'roster',
};

export function FlagReasons({ fq }) {
  return (
    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
      {flagReasonsOf(fq).map((r) => {
        const { label, color } = flagReasonLabel(r);
        return <Text key={r} testID={`flag-reason-${r}`} style={{ color, fontSize: 11, fontWeight: '600' }}>{label}</Text>;
      })}
    </View>
  );
}

/**
 * Candidates the search could have meant. `onUse(index)` confirms candidate
 * #index for this query text (entityResolutions action confirmFlaggedCandidate).
 */
export function FlaggedCandidates({ fq, onUse, busyIndex = null }) {
  const cands = Array.isArray(fq?.candidates) ? fq.candidates : [];
  if (!cands.length) return null;
  const resolved = fq.resolved_candidate;
  return (
    <View style={{ marginTop: 8 }}>
      <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4 }}>
        {resolved ? `Answered: ${resolved.name}` : 'What did they mean? One click applies it to everyone.'}
      </Text>
      {fq.flag_details?.[0] ? (
        <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 4 }}>{fq.flag_details[0]}</Text>
      ) : null}
      {cands.map((c, i) => {
        const isAnswer = resolved && resolved.name === c.name && resolved.type === c.type;
        const meta = [c.type, c.team, c.league ? String(c.league).toUpperCase() : null, SOURCE_LABELS[c.source] || c.source]
          .filter(Boolean).join(' · ');
        return (
          <View key={`${c.type}-${c.name}-${i}`} testID={`flag-candidate-${i}`}
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingVertical: 4, borderTopWidth: i ? 1 : 0, borderTopColor: 'rgba(255,255,255,0.06)' }}>
            <View style={{ flex: 1 }}>
              <Text style={{ color: isAnswer ? '#10b981' : '#fff', fontSize: 13, fontWeight: '600' }}>{c.name}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>{meta}</Text>
            </View>
            {resolved ? null : (
              <TouchableOpacity accessibilityRole="button" accessibilityLabel={`Use ${c.name}`} testID={`use-candidate-${i}`}
                disabled={busyIndex !== null} onPress={() => onUse && onUse(i)}
                style={{ backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5, opacity: busyIndex !== null && busyIndex !== i ? 0.5 : 1 }}>
                {busyIndex === i
                  ? <ActivityIndicator size="small" color="#10b981" />
                  : <Text style={{ color: '#10b981', fontSize: 11, fontWeight: '700' }}>Use</Text>}
              </TouchableOpacity>
            )}
          </View>
        );
      })}
    </View>
  );
}

/** Learned market aliases waiting for review: `onReview(id, 'confirmed' | 'rejected')`. */
export function PendingAliasList({ aliases, loading, onReview, busyId = null }) {
  if (loading) return <ActivityIndicator color="#fff" style={{ marginTop: 24 }} />;
  if (!aliases?.length) {
    return <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 12 }}>No market aliases waiting for review.</Text>;
  }
  return (
    <View>
      {aliases.map((a) => (
        <View key={a.id} testID={`alias-${a.id}`}
          style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)' }}>
          <Text style={{ color: '#fff', fontSize: 14, fontWeight: '600' }}>"{a.alias}" → {a.canonical}</Text>
          <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>
            {[`${a.usage_count || 0}× from the AI`, a.league ? String(a.league).toUpperCase() : null, a.sport].filter(Boolean).join(' · ')}
          </Text>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
            <TouchableOpacity accessibilityRole="button" accessibilityLabel={`Confirm ${a.alias}`} testID={`alias-confirm-${a.id}`}
              disabled={busyId !== null} onPress={() => onReview && onReview(a.id, 'confirmed')}
              style={{ backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 }}>
              <Text style={{ color: '#10b981', fontSize: 11, fontWeight: '700' }}>Confirm</Text>
            </TouchableOpacity>
            <TouchableOpacity accessibilityRole="button" accessibilityLabel={`Reject ${a.alias}`} testID={`alias-reject-${a.id}`}
              disabled={busyId !== null} onPress={() => onReview && onReview(a.id, 'rejected')}
              style={{ backgroundColor: 'rgba(239,68,68,0.15)', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 }}>
              <Text style={{ color: '#ef4444', fontSize: 11, fontWeight: '700' }}>Reject</Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </View>
  );
}
